import type { Category, CategoryFilter } from "../../shared/types";
import { useCategories } from "../../shared/stores/category-store";

interface CategoryPillsProps {
  readonly active: CategoryFilter;
  readonly onChange: (category: CategoryFilter) => void;
  readonly counts: Record<Category, number>;
  readonly totalCount: number;
  readonly completionRates?: Record<Category, number>;
}

export function CategoryPills({
  active,
  onChange,
  counts,
  totalCount,
  completionRates,
}: CategoryPillsProps) {
  const { categories, labels, dotColors } = useCategories();
  const pills: CategoryFilter[] = ["all", ...categories] as CategoryFilter[];

  return (
    <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory cursor-grab active:cursor-grabbing select-none scroll-smooth">
      {pills.map((category) => {
        const isAll = category === "all";
        const count = isAll
          ? totalCount
          : counts[category as unknown as Category];
        const isActive = active === category;
        const label = isAll ? "Tout" : labels[category];
        const dotColor = isAll ? "bg-accent" : dotColors[category];
        const completionRate = isAll ? null : (completionRates?.[category as Category] ?? 0);

        return (
          <button
            key={category}
            onClick={() => onChange(category)}
            className="group relative flex flex-col items-center gap-1 px-2 pt-1 pb-2 rounded-md text-xs font-medium transition-all shrink-0 snap-start"
          >
            <div className={`flex items-center gap-1.5 ${
              isActive ? "text-ink" : "text-ink-muted hover:text-ink hover:bg-surface-alt/50 rounded px-1 -mx-1 py-0.5"
            }`}>
              {/* Dot de couleur */}
              <div className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />

              {/* Label */}
              <span>{label}</span>

              {/* Count */}
              {count > 0 && (
                <span className={`text-[10px] font-medium ${
                  isActive ? "text-ink/70" : "text-ink-faint"
                }`}>
                  {count}
                </span>
              )}
            </div>

            {/* Barre de progression pour les catégories */}
            {!isAll && count > 0 && (
              <div className="absolute bottom-1 left-1 right-1 h-0.5 bg-border/20 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${dotColor}`}
                  style={{ width: `${completionRate}%` }}
                />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
